import { useCallback, useEffect, useRef } from 'react'

interface PrintWindowOptions {
  onError?: (error: unknown) => void
  onPrintFinished?: () => void
}

interface PrintWindowArgs {
  html?: string
  loadHtml?: () => Promise<string>
  title?: string
  autoPrint?: boolean
  features?: string | null
}

export function usePrintWindow(options: PrintWindowOptions = {}) {
  const { onError, onPrintFinished } = options

  // The print finished event can fire multiple times,
  // so we need to keep track of whether we've already handled it
  const onPrintFinishedRef = useRef(onPrintFinished)
  const hasHandledPrintFinished = useRef(false)

  // We also could have multiple instances of the hook on the same page
  // so we need to keep track of the instance ID
  const instanceRef = useRef<string | number | undefined>(undefined)

  useEffect(() => {
    onPrintFinishedRef.current = onPrintFinished
  }, [onPrintFinished])

  // Listen for "PRINT_DONE" messages from the print window
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const data: unknown = event.data
      if (
        typeof data === 'object' &&
        data !== null &&
        'type' in data &&
        'token' in data &&
        data.type === 'PRINT_DONE' &&
        // Only handle the message if it's for the current instance
        data.token === instanceRef.current &&
        // ...and only handle it once
        !hasHandledPrintFinished?.current
      ) {
        hasHandledPrintFinished.current = true
        onPrintFinishedRef?.current?.()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const openPrintWindow = useCallback(
    async function (args: PrintWindowArgs = {}): Promise<void> {
      const {
        html: providedHtml,
        loadHtml,
        title,
        autoPrint = true,
        // Set `features` to null and you'll get a new tab instead of a popup window.
        // Either way, it will be auto-closed once they hit print/cancel on the print dialog.
        features = 'width=800,height=600',
      } = args

      // Reset this flag each time we open a new print window
      hasHandledPrintFinished.current = false

      const token = window?.crypto?.randomUUID() || Date.now()
      instanceRef.current = token

      // Time to kick it like it's 1999...JavaScript style

      // Open window synchronously (must be inside a click handler)
      const printWindow = window.open('', '_blank', features ?? undefined)
      if (!printWindow) {
        return onError?.(new Error('Popup blocked: cannot open print window.'))
      }

      try {
        // Get HTML either from url or passed directly
        let html: string

        if (loadHtml) {
          html = await loadHtml()
        } else if (providedHtml !== undefined) {
          html = providedHtml
        } else {
          throw new Error('You must provide either `loadHtml` or `html`.')
        }

        // Ensure HTML is a full document
        const hasHtmlTag = /<html[\s>]/i.test(html)
        const hasBodyTag = /<body[\s>]/i.test(html)

        let finalHtml = html

        if (!hasHtmlTag) {
          // Wrap as a full HTML doc
          finalHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="UTF-8">
                ${title ? `<title>${title}</title>` : ''}
              </head>
              <body>
                ${html}
              </body>
            </html>
          `
        } else if (title) {
          // Insert/replace <title>
          if (/<title>.*<\/title>/i.test(finalHtml)) {
            finalHtml = finalHtml.replace(/<title>.*<\/title>/i, `<title>${title}</title>`)
          } else if (/<head[\s>]/i.test(finalHtml)) {
            finalHtml = finalHtml.replace(/<head[\s>]/i, `<head><title>${title}</title>`)
          }
        }

        // Inject the auto-print script if `autoPrint` is true
        if (autoPrint) {
          const autoPrintScript = /* HTML */ `
            <script>
              ;(() => {
                const done = () => {
                  try {
                    window.opener?.postMessage({ type: 'PRINT_DONE', token: '${token}' }, '*')
                  } catch (e) {}
                  window.close()
                }
                const finish = () => setTimeout(done, 0) // Firefox needs next tick

                // Close when printing finishes (Chrome, Edge, etc.)
                window.addEventListener?.('afterprint', finish)

                // Extra safety: some browsers fire via matchMedia('print')
                try {
                  const mql = window.matchMedia?.('print')
                  mql?.addEventListener?.('change', (e) => !e.matches && finish())
                  mql?.addListener?.((e) => !e.matches && finish()) // older API
                } catch (e) {}

                window.addEventListener('load', () => {
                  try {
                    window.focus?.()
                  } catch (e) {}
                  window.print()
                })
              })()
            </script>
          `

          if (hasBodyTag) {
            finalHtml = finalHtml.replace(/<\/body>/i, autoPrintScript + '</body>')
          } else {
            finalHtml = finalHtml + autoPrintScript
          }
        }

        // Create blob URL and navigate new window
        // (this avoids using the deprecated window.write() method, or the janky iframe method)
        const blob = new Blob([finalHtml], { type: 'text/html' })
        const blobUrl = URL.createObjectURL(blob)

        printWindow.location.href = blobUrl

        // Clean up after ourselves
        printWindow.addEventListener('load', () => {
          URL.revokeObjectURL(blobUrl)
        })
      } catch (err) {
        onError?.(err)
        printWindow.close()
      }
    },
    [onError]
  )

  return { openPrintWindow }
}
